/**
 * API Response Types for One Payments
 *
 * This module provides TypeScript interfaces for all API responses from the One Payments backend.
 * These types ensure compile-time type safety and enable excellent IDE autocomplete when working
 * with API responses throughout the SDK.
 *
 * All API responses follow a standard wrapper format with `status` and `result` fields.
 *
 * @module @one-payments/core/types/api
 * @since 1.0.0
 * @public
 */

/**
 * Standard API response wrapper
 *
 * All One Payments API endpoints return responses in this standardized format,
 * with a status string and a strongly-typed result object.
 *
 * @typeParam T - The type of the result object (varies by endpoint)
 *
 * @example
 * ```typescript
 * // Payment intent creation response
 * const response: ApiResponse<CreatePaymentIntentResult> = {
 *   status: "success",
 *   result: {
 *     redirectUrl: "https://checkout.example.com?token=abc123",
 *     paymentIntentId: "pi_1234567890"
 *   }
 * };
 * ```
 *
 * @public
 */
export interface ApiResponse<T> {
  /**
   * Response status indicator
   *
   * @example "success"
   */
  status: string;

  /**
   * Response payload containing the requested data
   */
  result: T;
}

/**
 * Payment intent creation response
 *
 * Returned by the POST /payment-intent/create endpoint after successfully
 * creating a new payment intent. Contains a redirect URL with an embedded
 * payment token and the payment intent ID.
 *
 * @example
 * ```typescript
 * const result: CreatePaymentIntentResult = {
 *   redirectUrl: "https://checkout.one.ooo?token=xyz789",
 *   paymentIntentId: "pi_1234567890"
 * };
 *
 * // Extract token from redirectUrl
 * const url = new URL(result.redirectUrl);
 * const token = url.searchParams.get('token');
 * ```
 *
 * @public
 */
export interface CreatePaymentIntentResult {
  /**
   * URL to redirect user to for payment completion
   * Contains the payment token as a query parameter
   *
   * @example "https://checkout.example.com?token=abc123"
   */
  redirectUrl: string;

  /**
   * Unique identifier for this payment intent
   *
   * @example "pi_1234567890"
   */
  paymentIntentId: string;
}

/**
 * Fee configuration object
 *
 * Describes a single fee applied to a payment transaction. Fees can be
 * percentage-based or fixed amounts, and may be owned by different parties
 * (merchant, platform, processor, etc.).
 *
 * @example
 * ```typescript
 * const processingFee: FeeConfig = {
 *   owner: "platform",
 *   type: "percentage",
 *   value: 2.9,  // 2.9%
 *   currency: "USD"
 * };
 *
 * const fixedFee: FeeConfig = {
 *   owner: "processor",
 *   type: "fixed",
 *   value: 30,  // $0.30
 *   currency: "USD"
 * };
 * ```
 *
 * @public
 */
export interface FeeConfig {
  /**
   * Who owns/receives this fee
   *
   * @example "merchant" | "platform" | "processor"
   */
  owner: string;

  /**
   * Fee calculation type
   *
   * @example "percentage" | "fixed"
   */
  type: string;

  /**
   * Fee value (percentage as decimal or fixed amount in cents)
   *
   * @example 2.5 (for 2.5%) or 50 (for $0.50)
   */
  value: number;

  /**
   * Currency code for fixed fees
   *
   * @example "USD"
   */
  currency: string;
}

/**
 * Payment data response
 *
 * Returned by the GET /payment-intent/get-payment-data endpoint. Contains
 * comprehensive payment information including amounts, fees, status, and
 * optional action URLs or QR codes for payment completion.
 *
 * This is the most frequently used response type, as it's called during both
 * initialization and status polling.
 *
 * @example
 * ```typescript
 * const paymentData: PaymentDataResult = {
 *   orderId: "ORDER-123",
 *   currency: "USD",
 *   initialAmount: 5000,      // $50.00 original
 *   customerAmount: 5145,     // $51.45 with fees
 *   merchantAmount: 5000,     // $50.00 after fees
 *   totalFee: 145,            // $1.45 total fees
 *   totalSystemFee: 145,
 *   status: "in_progress",
 *   paymentIntentId: "pi_abc123",
 *   fees: [
 *     { owner: "platform", type: "percentage", value: 2.9, currency: "USD" }
 *   ]
 * };
 * ```
 *
 * @public
 */
export interface PaymentDataResult {
  /**
   * Order identifier from payment creation
   */
  orderId: string;

  /**
   * Payment currency code (ISO 4217)
   */
  currency: string;

  /**
   * Original payment amount before fees (in cents)
   */
  initialAmount: number;

  /**
   * Amount customer will pay including fees (in cents)
   */
  customerAmount: number;

  /**
   * Amount merchant will receive after fees (in cents)
   */
  merchantAmount: number;

  /**
   * Total fees applied (in cents)
   */
  totalFee: number;

  /**
   * System/platform fees (in cents)
   */
  totalSystemFee: number;

  /**
   * Detailed fee breakdown
   */
  fees?: FeeConfig[];

  /**
   * Current payment status
   */
  status: string;

  /**
   * Optional 3DS authentication URL
   * Present when 3D Secure verification is required
   */
  nextActionUrl?: string;

  /**
   * Optional QR code data for PayNow payments
   * Can be a base64 image or URL to QR page
   */
  qrCode?: string;

  /**
   * Optional custom metadata
   */
  metadata?: Record<string, string | number | boolean>;

  /**
   * Payment intent ID
   */
  paymentIntentId?: string;

  /**
   * Payment amount
   */
  amount?: number;

  /**
   * Session ID for fraud prevention
   * Used to initialize Airwallex device fingerprinting
   */
  sessionId?: string;

  /**
   * Error information if payment failed
   */
  error?:
    | {
        message: string;
        code?: string;
      }
    | string;
}

/**
 * Payment method configuration from API
 *
 * Describes an available payment method
 */
export interface ApiPaymentMethod {
  /**
   * Method type identifier
   *
   * @example "Card Payment" | "Bank Transfer"
   */
  type: string;

  /**
   * Display label for UI
   *
   * @example "Credit/Debit Card"
   */
  label: string;

  /**
   * List of supported capabilities
   */
  capabilities: string[];
}

/**
 * Available payment methods response
 *
 * Returned by GET /payment-intent/available-methods
 */
export interface AvailableMethodsResult {
  /**
   * Array of payment methods enabled for this merchant/currency
   */
  paymentMethods: ApiPaymentMethod[];
}

/**
 * Card details for payment confirmation
 */
export interface CardDetails {
  /**
   * Card number (no spaces or dashes)
   *
   * @example "4242424242424242"
   */
  number: string;

  /**
   * Expiration month (2 digits)
   *
   * @example "12"
   */
  expiryMonth: string;

  /**
   * Expiration year (4 digits)
   *
   * @example "2025"
   */
  expiryYear: string;

  /**
   * Card verification code (3-4 digits)
   *
   * @example "123"
   */
  cvc: string;
}

/**
 * Card payment method data
 */
export interface CardPaymentMethod {
  type: 'card';
  card: CardDetails;
}

/**
 * QR-based payment method data (PayNow, etc.)
 */
export interface QRPaymentMethod {
  type: 'qr';
}

/**
 * Prompt Pay payment method data (Thailand)
 * Uses external redirect for QR code display
 */
export interface PromptPayPaymentMethod {
  type: 'prompt_pay';
}

/**
 * DuitNow payment method data (Malaysia)
 * Uses external redirect for QR code display
 */
export interface DuitNowPaymentMethod {
  type: 'duit_now';
}

/**
 * GoPay payment method data (Indonesia)
 * Uses external redirect to Gojek app
 */
export interface GoPayPaymentMethod {
  type: 'go_pay';
}

/**
 * Boost payment method data (Malaysia)
 * Uses external redirect to Boost app
 */
export interface BoostPaymentMethod {
  type: 'boost';
}

/**
 * ShopeePay payment method data (Southeast Asia)
 * Uses external redirect to Shopee app
 */
export interface ShopeePayPaymentMethod {
  type: 'shopee_pay';
}

/**
 * Atome payment method data (Singapore/Malaysia)
 * Uses external redirect for payment
 */
export interface AtomePaymentMethod {
  type: 'atome';
}

/**
 * Dana payment method data (Indonesia)
 * Uses external redirect for payment
 */
export interface DanaPaymentMethod {
  type: 'dana';
}

/**
 * Touch 'n Go payment method data (Malaysia)
 * Uses external redirect for payment
 */
export interface TngPaymentMethod {
  type: 'tng';
}

/**
 * Alipay CN payment method data (China)
 * Uses external redirect for payment
 */
export interface AlipayCNPaymentMethod {
  type: 'alipaycn';
}

/**
 * Alipay HK payment method data (Hong Kong)
 * Uses external redirect for payment
 */
export interface AlipayHKPaymentMethod {
  type: 'alipayhk';
}

/**
 * GCash payment method data (Philippines)
 * Uses external redirect for payment
 */
export interface GCashPaymentMethod {
  type: 'gcash';
}

/**
 * Konbini payment method data (Japan)
 * Uses external redirect for convenience store payment
 */
export interface KonbiniPaymentMethod {
  type: 'konbini';
}

/**
 * Pay-Easy payment method data (Japan)
 * Uses external redirect for bank transfer/ATM payment
 */
export interface PayEasyPaymentMethod {
  type: 'payeasy';
}

/**
 * GrabPay payment method data (Singapore)
 * Uses external redirect for payment
 */
export interface GrabPaySGPaymentMethod {
  type: 'grabpay_sg';
}

/**
 * FPX payment method data (Malaysia)
 * Uses external redirect to bank selection page
 */
export interface FPXPaymentMethod {
  type: 'fpx';
}

/**
 * Bank information for bank selection
 */
export interface Bank {
  /** Unique identifier for the bank (used in confirm request) */
  bankName: string;
  /** Human-readable bank name (shown in UI) */
  displayName: string;
  /** URL to the bank's logo image */
  logoUrl: string;
}

/**
 * Banks API response
 */
export interface BanksResult {
  banks: Bank[];
}

/**
 * Payment method union type
 */
export type PaymentMethodData =
  | CardPaymentMethod
  | QRPaymentMethod
  | PromptPayPaymentMethod
  | DuitNowPaymentMethod
  | GoPayPaymentMethod
  | BoostPaymentMethod
  | ShopeePayPaymentMethod
  | AtomePaymentMethod
  | DanaPaymentMethod
  | TngPaymentMethod
  | AlipayCNPaymentMethod
  | AlipayHKPaymentMethod
  | GCashPaymentMethod
  | KonbiniPaymentMethod
  | PayEasyPaymentMethod
  | GrabPaySGPaymentMethod
  | FPXPaymentMethod;

/**
 * Payment confirmation request body
 *
 * Sent to POST /payment-intent/confirm (PATCH)
 */
export interface ConfirmPaymentRequest {
  /**
   * Customer's first name
   */
  firstName: string;

  /**
   * Customer's last name
   */
  lastName: string;

  /**
   * Customer's email address
   */
  email: string;

  /**
   * Customer's phone number
   */
  phoneNumber: string;

  /**
   * Selected payment method details
   */
  paymentMethod: PaymentMethodData;

  /**
   * Bank name for FPX and similar bank selection payment methods
   * Required when payment method requires bank selection
   */
  bankName?: string;
}

/**
 * Error response structure from HTTP adapters
 *
 * This is the structure that might be thrown as an error
 */
export interface HttpError extends Error {
  response?: {
    data?: {
      message?: string;
      code?: string;
      error?: {
        message?: string;
        code?: string;
      };
    };
  };
  data?: {
    message?: string;
    code?: string;
    error?: {
      message?: string;
      code?: string;
    };
  };
}

/**
 * Type guard to check if error has response data structure
 *
 * @param error - Error to check
 * @returns True if error has response.data or data property
 */
export function isHttpError(error: unknown): error is HttpError {
  return (
    error instanceof Error &&
    (('response' in error && typeof error.response === 'object' && error.response !== null) ||
      ('data' in error && typeof error.data === 'object' && error.data !== null))
  );
}
